import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { pigeonClient } from '../../apiUtils/client';

interface BrowserbaseSession {
  id: string;
  connectUrl: string;
  status: 'running' | 'stopped' | 'error';
  createdAt: string;
  expiresAt: string;
}



interface CreateSessionRequest {
  carrierSlug: string;
  clientId: string;
}

export function useBrowserbase() {
  const [activeSession, setActiveSession] = useState<BrowserbaseSession | null>(null);

  const createSessionMutation = useMutation({
    mutationFn: async (data: CreateSessionRequest): Promise<BrowserbaseSession> => {
      const response = await pigeonClient.client.post<BrowserbaseSession>('/browserbase/session', data);
      return response.data;
    },
    onSuccess: (session) => {
      setActiveSession(session);
    },
    onError: (error) => {
      console.error('Failed to create Browserbase session:', error);
    },
  });

  const getSessionStatusMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await pigeonClient.client.get<BrowserbaseSession>(`/browserbase/session/${sessionId}/status`);
      return response.data;
    },
  });

  const closeSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await pigeonClient.client.delete<void>(`/browserbase/session/${sessionId}`);
      return response.data;
    },
    onSuccess: () => {
      setActiveSession(null);
    },
  });

  const createSession = async (carrierSlug: string, clientId: string) => {
    return createSessionMutation.mutateAsync({ carrierSlug, clientId });
  };

  const getSessionStatus = async (sessionId: string) => {
    return getSessionStatusMutation.mutateAsync(sessionId);
  };

  const closeSession = async (sessionId: string) => {
    return closeSessionMutation.mutateAsync(sessionId);
  };

  const openSessionInNewTab = (session: BrowserbaseSession) => {
    if (session.connectUrl) {
      // For Browserbase use debugger URL if it available
      const debuggerUrl = `https://www.browserbase.com/sessions/${session.id}`;
      window.open(debuggerUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return {
    activeSession,
    createSession,
    getSessionStatus,
    closeSession,
    openSessionInNewTab,
    isCreatingSession: createSessionMutation.isPending,
    isClosingSession: closeSessionMutation.isPending,
    createSessionError: createSessionMutation.error,
    closeSessionError: closeSessionMutation.error,
  };
}


